import type { ShiftType } from "@/entities/shift-template/model/types";

export const ShiftAssignmentStatus = {
  Pending: "Pending",
  Accepted: "Accepted",
  Rejected: "Rejected",
} as const;

export type ShiftAssignmentStatus =
  (typeof ShiftAssignmentStatus)[keyof typeof ShiftAssignmentStatus];

export const ShiftAssignmentStatusLabels: Record<
  ShiftAssignmentStatus,
  string
> = {
  [ShiftAssignmentStatus.Pending]: "Pending",
  [ShiftAssignmentStatus.Accepted]: "Accepted",
  [ShiftAssignmentStatus.Rejected]: "Rejected",
};

/**
 * A day as the API actually sends it: a name, not an index. The backend
 * registers `JsonStringEnumConverter` globally, so `System.DayOfWeek` comes
 * back as "Monday" and never as 1.
 *
 * It was typed here as a number, which no response ever matched — recurrence
 * days read back from the server compared equal to nothing, so a saved
 * recurring template opened with no days checked. The reason that went
 * unnoticed for so long is that the converter is registered with
 * `allowIntegerValues: true`: the numbers this app sent were accepted and
 * stored correctly, so creating a recurrence worked and only reading one back
 * was broken.
 *
 * Declaration order is the wire's own (Sunday first, matching .NET) and the
 * picker renders in `DayOfWeekLabels` insertion order, so it is load-bearing.
 */
export const DAY_OF_WEEK_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export type DayOfWeek = (typeof DAY_OF_WEEK_NAMES)[number];

export const DayOfWeekLabels: Record<DayOfWeek, string> = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
};

export interface ShiftCalendarResponse {
  id: number;
  title: string;
  startTime: string;
  endTime: string;
  shiftColor: string | null;
  siteId: number;
  categoryId: number | null;
  numberOfGuards: number;
  assignedGuardsCount: number;
  acceptedGuardsCount: number;
  shiftType: ShiftType;
  openForClaim: boolean;
  openSpots: number;
  siteTimeZone?: string;
  /** Optimistic-concurrency token; sent back on batch updates. */
  version: number;
  clientId: number;
  seriesId: string | null;
  isLocked: boolean;
  guards: ShiftCalendarGuard[];
}

export interface ShiftCalendarGuard {
  guardUserId: number;
  assignmentStatus: ShiftAssignmentStatus;
}

export type ShiftOrigin = "Assigned" | "Claimed";

export interface ShiftGuardResponse {
  guardUserId: number;
  assignmentStatus: ShiftAssignmentStatus;
  origin: ShiftOrigin;
}

export interface ShiftResponse {
  id: number;
  accountId: number;
  seriesId: string | null;
  title: string;
  shiftType: ShiftType;
  startTime: string;
  endTime: string;
  siteId: number;
  numberOfGuards: number;
  shiftColor: string | null;
  categoryId: number | null;
  department: string | null;
  skillSet: string | null;
  note: string | null;
  guards: ShiftGuardResponse[];
  recurrenceDays?: DayOfWeek[] | null;
  recurrenceWeeks?: number | null;
  createdOn: string;
  createdBy: string | null;
  retainedGuardUserIds?: number[];
  openForClaim: boolean;
  openSpots: number;
  siteTimeZone?: string;
}

export interface CreateShiftRequest {
  title: string;
  shiftType: ShiftType;
  startTime: string;
  endTime: string;
  siteId: number;
  numberOfGuards: number;
  shiftColor?: string | null;
  categoryId?: number | null;
  department?: string | null;
  skillSet?: string | null;
  note?: string | null;
  guardUserIds?: number[];
  recurrenceDays?: DayOfWeek[];
  recurrenceWeeks?: number;
  openForClaim?: boolean;
  guardRates?: { guardUserId: number; payRate: number }[];
}

export interface UpdateShiftRequest {
  title?: string;
  shiftType?: ShiftType;
  startTime?: string;
  endTime?: string;
  siteId?: number;
  numberOfGuards?: number;
  shiftColor?: string | null;
  categoryId?: number | null;
  department?: string | null;
  skillSet?: string | null;
  note?: string | null;
  guardUserIds?: number[];
  recurrenceDays?: DayOfWeek[];
  recurrenceWeeks?: number;
  updateEntireSeries?: boolean;
  openForClaim?: boolean;
  guardRates?: { guardUserId: number; payRate: number | null }[];
}

export interface ShiftCalendarParams {
  fromDate: string;
  toDate: string;
  clientId?: number;
  siteId?: number;
  categoryId?: number;
  guardUserId?: number;
  shiftType?: ShiftType;
  assignmentStatuses?: ShiftAssignmentStatus[];
}

export type ShiftBatchOperationKind = "Update" | "Create";

export interface ShiftBatchOperation {
  ref: string;
  kind: ShiftBatchOperationKind;
  shiftId?: number;
  version?: number;
  /** Site wall clock, "yyyy-MM-ddTHH:mm:ss", no offset. */
  startTime?: string;
  /** Site wall clock, "yyyy-MM-ddTHH:mm:ss", no offset. */
  endTime?: string;
  addGuardUserIds?: number[];
  removeGuardUserIds?: number[];
  copyOfShiftId?: number;
  templateId?: number;
  siteId?: number;
  guardUserIds?: number[];
}

export interface ShiftBatchRequest {
  operations: ShiftBatchOperation[];
}

/**
 * An inherited assignment the save left off because the person is no longer employed: a saved
 * template's default guard, or a copied shift's roster. The shift keeps the headcount it asked
 * for, so the hole shows in `openSpots` — this is the reason for it.
 *
 * A guard the office named (a drag, an explicit add) is an `EMPLOYMENT_ENDED` 422 instead and
 * never appears here.
 */
export interface ShiftBatchSkippedAssignment {
  ref: string;
  shiftId: number;
  guardUserId: number;
  guardName: string;
  reason: string;
}

export interface ShiftBatchResponse {
  created: { ref: string; shiftId: number }[];
  updated: { ref: string; shiftId: number; version: number }[];
  notifiedGuardCount: number;
  /** Empty in the normal case. */
  skippedAssignments?: ShiftBatchSkippedAssignment[];
}

export type ShiftBatchErrorCode =
  | "DOUBLE_BOOKED"
  | "CREDENTIAL_INVALID"
  | "EMPLOYMENT_ENDED"
  | "NO_CLIENT_ACCESS"
  | "SHIFT_LOCKED"
  | "SHIFT_CHANGED"
  | "OVER_CAPACITY"
  | "NOT_FOUND"
  | "VALIDATION_FAILED";

export interface ShiftBatchErrorMetadata {
  ref: string;
  code: ShiftBatchErrorCode;
  shiftId?: number;
  guardUserId?: number;
  conflictShiftId?: number;
}

export interface ShiftBatchError {
  propertyName: string;
  message: string;
  errorCode: string;
  metadata?: ShiftBatchErrorMetadata;
  traceId?: string;
  timestamp?: string;
}
